/// <reference types="vite/client" />
import { GoogleGenAI, Modality, GenerateContentResponse } from "@google/genai";
import { GeminiModel } from "../types";

export class GeminiService {
  private genAI: GoogleGenAI;

  constructor(apiKey: string) {
    if (!apiKey) {
      console.warn("[GeminiService] No API key provided.");
    }
    this.genAI = new GoogleGenAI({ apiKey });
  }

  private stripModelsPrefix(modelId: string): string {
    return modelId.startsWith('models/') ? modelId.replace('models/', '') : modelId;
  }

  private getClient() {
    return this.genAI;
  }

  async generateText(
    prompt: string,
    systemInstruction: string,
    model: string,
    useSearch: boolean = false,
    responseSchema?: any,
    thinkingBudget: number = 0
  ) {
    // Self-healing: mapped legacy/invalid names to valid current models
    const modelMapping: Record<string, string> = {
      'gemini-3-flash': GeminiModel.FLASH_3,
      'gemini-3-pro': GeminiModel.PRO_3,
      'gemini-3-thinking': GeminiModel.THINKING_3,
      'gemini-3-lite': GeminiModel.LITE_3,
      'gemini-3-flash-preview': GeminiModel.FLASH_3,
      'gemini-2.0-flash-001': GeminiModel.FLASH_3,
      'gemini-2.0-pro-exp-02-05': GeminiModel.PRO_3,
      'gemini-2.0-flash-lite-preview-02-05': GeminiModel.LITE_3,
      'gemini-2.0-flash-thinking-exp-01-21': GeminiModel.THINKING_3,
      'gemini-1.5-flash': GeminiModel.FLASH_3,
      'gemini-1.5-pro': GeminiModel.PRO_3
    };

    // Only apply mapping if it looks like a Gemini model or is in the mapping table
    // and doesn't contain slashes (which usually indicate OpenRouter/local models)
    const strippedModel = this.stripModelsPrefix(model || GeminiModel.FLASH_3);
    let targetModel = modelMapping[strippedModel] ?? strippedModel;
    if (targetModel.includes('/') || targetModel.includes('\\') || targetModel.includes(':')) {
      targetModel = GeminiModel.FLASH_3;
    }

    const modelId = this.stripModelsPrefix(targetModel || GeminiModel.FLASH_3);
    const ai = this.getClient();
    try {
      const shouldUseSchemaConfig = Boolean(responseSchema) && !useSearch;
      const finalPrompt = responseSchema && useSearch
        ? `${prompt}\n\nGeef uitsluitend valide JSON terug die exact past bij de gevraagde structuur. Gebruik geen markdown of extra uitleg.`
        : prompt;

      const config: any = {
        systemInstruction: { parts: [{ text: systemInstruction }] },
        temperature: 0.2,
      };

      if (useSearch) {
        config.tools = [{ googleSearch: {} }];
      }

      if (shouldUseSchemaConfig) {
        config.responseMimeType = "application/json";
        config.responseSchema = responseSchema;
      }

      if (thinkingBudget > 0 && (modelId.includes('gemini-3') || modelId.includes('gemini-2.5'))) {
        config.thinkingConfig = { thinkingBudget };
      }

      const response: GenerateContentResponse = await ai.models.generateContent({
        model: modelId,
        contents: finalPrompt,
        config,
      });

      const rawText = response.text || "";

      if (responseSchema) {
        return {
          text: this.extractJson(rawText),
          groundingChunks: response.candidates?.[0]?.groundingMetadata?.groundingChunks || []
        };
      }

      return {
        text: rawText,
        groundingChunks: response.candidates?.[0]?.groundingMetadata?.groundingChunks || []
      };
    } catch (error) {
      console.error("Gemini Text Error:", error);
      throw error;
    }
  }

  private extractJson(text: string): string {
    try {
      JSON.parse(text);
      return text;
    } catch (e) { /* ignore */ }

    const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (jsonMatch && jsonMatch[1]) {
      try {
        const cleaned = jsonMatch[1].trim();
        JSON.parse(cleaned);
        return cleaned;
      } catch (e) { /* ignore */ }
    }

    const startBrace = text.indexOf('{');
    const startBracket = text.indexOf('[');
    const start = (startBrace !== -1 && (startBracket === -1 || startBrace < startBracket)) ? startBrace : startBracket;

    const endBrace = text.lastIndexOf('}');
    const endBracket = text.lastIndexOf(']');
    const end = (endBrace !== -1 && (endBracket === -1 || endBrace > endBracket)) ? endBrace : endBracket;

    if (start !== -1 && end !== -1 && end > start) {
      const candidate = text.substring(start, end + 1);
      try {
        JSON.parse(candidate);
        return candidate;
      } catch (e) { /* ignore */ }
    }

    return text;
  }

  async generateTTS(text: string, voiceName: string = 'Zephyr') {
    const ai = this.getClient();
    try {
      const response: GenerateContentResponse = await ai.models.generateContent({
        model: 'gemini-2.5-flash-preview-tts',
        contents: [{ parts: [{ text }] }],
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName },
            },
          },
        },
      });
      return response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    } catch (error) {
      console.error("Gemini TTS Error:", error);
      throw error;
    }
  }

  async connectLive(callbacks: any, systemInstruction: string, voiceName: string = 'Zephyr', model: string = GeminiModel.LIVE) {
    const ai = this.getClient();
    const modelId = this.stripModelsPrefix(model); // Apply stripModelsPrefix here
    return ai.live.connect({
      model: modelId, // Use the stripped modelId
      callbacks,
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName } },
        },
        systemInstruction: { parts: [{ text: systemInstruction }] },
      },
    });
  }
}

export function decodeBase64(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function encodeUint8(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number = 24000,
  numChannels: number = 1,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}
